import { Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";

import { LoadingButton } from "../../components/LoadingButton";
import { ShiftQualificationPill } from "../../Shift/QualificationPill";

interface ShiftHistoryCardFooterProps {
  totalPayInDollars: number;
  qualificationText: string;
  isVerified: boolean;
  shouldDisableButton: boolean;
  isMissedPunchRequestEnabled: boolean;
  hasPendingExtraWorkedTimeRequest: boolean;
  isTimesheetUploaded: boolean;
  isRequestingShiftAdjustment: boolean;
  onUploadTimesheetClick: () => void;
  onChatWithSupportClick: () => void;
  onRequestShiftAdjustmentClick: () => void;
}

export function ShiftHistoryCardFooter(props: ShiftHistoryCardFooterProps) {
  const {
    totalPayInDollars,
    qualificationText: qualificationName,
    isVerified,
    shouldDisableButton,
    isMissedPunchRequestEnabled,
    hasPendingExtraWorkedTimeRequest,
    isTimesheetUploaded,
    isRequestingShiftAdjustment,
    onUploadTimesheetClick,
    onChatWithSupportClick,
    onRequestShiftAdjustmentClick,
  } = props;

  const showEarnedPay = totalPayInDollars > 0 && isVerified;

  function getButtonConfig(): {
    label: string;
    variant: "outlined" | "contained";
    onClick?: () => void;
    isLoading?: boolean;
  } {
    if (hasPendingExtraWorkedTimeRequest) {
      return {
        label: "Payment request in review",
        variant: "outlined",
      };
    }

    if (isMissedPunchRequestEnabled) {
      return {
        label: isVerified ? "Request pay adjustment" : "Request shift payment",
        variant: isVerified ? "outlined" : "contained",
        onClick: onRequestShiftAdjustmentClick,
        isLoading: isRequestingShiftAdjustment,
      };
    }

    if (isVerified) {
      return {
        label: "Request pay adjustment",
        variant: "outlined",
        onClick: onChatWithSupportClick,
      };
    }

    return {
      label: "Request shift payment",
      variant: isTimesheetUploaded ? "outlined" : "contained",
      onClick: onUploadTimesheetClick,
    };
  }

  const buttonConfig = getButtonConfig();

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <ShiftQualificationPill qualificationText={qualificationName} />

        <Text semibold variant="h5">
          {showEarnedPay && `${formatDollarsAsUsd(totalPayInDollars)} earned`}
        </Text>
      </Stack>
      <Box sx={{ pt: 4 }}>
        <Stack direction="column" alignItems="center">
          <LoadingButton
            fullWidth
            variant={buttonConfig.variant}
            size="medium"
            disabled={hasPendingExtraWorkedTimeRequest || shouldDisableButton}
            isLoading={buttonConfig.isLoading ?? false}
            onClick={buttonConfig.onClick}
          >
            {buttonConfig.label}
          </LoadingButton>
        </Stack>
      </Box>
    </Stack>
  );
}
